export interface LayerControlHeightItem {
  /** Whether this layer's nested group is expanded, revealing its children. */
  isExpanded?: boolean;
  /** Child layers rendered underneath this layer when expanded (e.g. S2S events, shape groups). */
  children?: unknown[];
}

export interface CalculateLayerControlContentHeightOptions {
  /** Top-level layers currently rendered in the layer control list. */
  layers: LayerControlHeightItem[];
  /** Height (px) of a single layer row (parent or child). */
  layerHeight?: number;
  /** Gap (px) between top-level draggable rows. */
  gapSize?: number;
  /** Bottom padding/shadow breathing room (px). */
  bottomPadding?: number;
  /** Maximum content height (px) before the list starts scrolling. */
  maxHeight?: number;
}

/**
 * Computes the layer control's content height so it grows to fit visible
 * rows, including rows contributed by expanded nested groups (e.g. S2S
 * events, shape groups). Without counting expanded children, the panel stays
 * sized for the collapsed (top-level-only) row count and clips or forces
 * internal scrolling as soon as a group is folded out.
 */
export function calculateLayerControlContentHeight({
  layers,
  layerHeight = 36,
  gapSize = 12,
  bottomPadding = 18,
  maxHeight = 384,
}: CalculateLayerControlContentHeightOptions): number | 'auto' {
  const totalItems = layers.length;
  if (totalItems === 0) return 'auto';

  const totalRows = layers.reduce((rows, layer) => {
    const expandedChildCount =
      layer.isExpanded && layer.children ? layer.children.length : 0;
    return rows + 1 + expandedChildCount;
  }, 0);

  // Gaps only occur between top-level draggable rows, not between a group's children.
  const totalHeight =
    totalRows * layerHeight + (totalItems - 1) * gapSize + bottomPadding;

  return Math.min(totalHeight, maxHeight);
}
